import { computed, Injector, runInInjectionContext, signal, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { catchError, combineLatest, distinctUntilChanged, of, switchMap, take } from 'rxjs';
import { itemWorkspace } from './item-workspace-scope';

/** One accepted identity lookup per list-heading pipe/context; tenant changes reuse it. */
export function listWorkspaceScope(injector: Injector): Signal<'team' | 'mine' | undefined> {
  const context = injector.get(SpaceContextStore, null);
  const state = injector.get(BaseAppStateService, null);
  if (!context || !state) return signal(undefined);
  const resolver = runInInjectionContext(injector, () => new WorkspaceIdentityService());
  const workspace = toSignal(
    combineLatest([context.contextId$, context.type$]).pipe(
      distinctUntilChanged(([previousId, previousType], [id, type]) => previousId === id && previousType === type),
      switchMap(([contextId, type]) =>
        type === SpaceType.SHARED_SPACE && !!contextId
          ? combineLatest({
              identity: resolver.current().pipe(
                take(1),
                catchError(() => of(undefined)),
              ),
              tenant: state.getTeam(),
            })
          : of(undefined),
      ),
    ),
    { injector },
  );
  return computed(() =>
    itemWorkspace(workspace()?.identity, { id: 'list', accountId: workspace()?.tenant ?? undefined }),
  );
}
